import React, { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import { doc, FieldPath, setDoc } from "firebase/firestore";

import OnboardingBubble from "../components/general/onboarding/OnboardingBubble";
import { firestore } from "../firebase/firebase";
import { useFetchCoach } from "../hooks/useFetchCoach";
import { clearOnboardingProgress, readOnboardingProgress, writeOnboardingProgress } from "./storage";
import { onboardingTours } from "./tours";
import { OnboardingButtonAction, OnboardingStatus, OnboardingStep, OnboardingTour, StoredOnboardingProgress } from "./types";

const DEBUG_ONBOARDING_LOGS = false;
const BUBBLE_TRANSITION_DELAY_MS = 750;

const onboardingLog = (...args: unknown[]) => {
    if (!DEBUG_ONBOARDING_LOGS) return;
    console.log(...args);
};

const onboardingWarn = (...args: unknown[]) => {
    if (!DEBUG_ONBOARDING_LOGS) return;
    console.warn(...args);
};

interface OnboardingContextValue {
    activeTourId: string | null;
    activeStep: OnboardingStep | null;
    status: OnboardingStatus;
    startTour: (tourId: string, options?: { force?: boolean; stepIndex?: number; status?: OnboardingStatus }) => boolean;
    markStepCompleted: (tourId: string, stepId: string) => void;
    goToStep: (stepIndex: number, options?: { status?: OnboardingStatus }) => void;
    nextStep: () => void;
    prevStep: () => void;
    pauseTour: () => void;
    resumeTour: () => void;
    dismissTour: (tourId?: string) => void;
    completeTour: (tourId?: string) => void;
    restartTour: (tourId: string) => boolean;
    registerActionHandler: (tourId: string, actionId: string, handler: () => void) => void;
    unregisterActionHandler: (tourId: string, actionId: string) => void;
}

const OnboardingContext = createContext<OnboardingContextValue | undefined>(undefined);

// Tour/config helpers
const getTour = (tourId: string | null | undefined): OnboardingTour | null => {
    if (!tourId) return null;
    return onboardingTours[tourId] ?? null;
};

const getStepIndexById = (tour: OnboardingTour, stepId: string) => tour.steps.findIndex(step => step.id === stepId);

const getCompletedStepIdsForIndex = (tour: OnboardingTour, stepIndex: number) => tour.steps.slice(0, Math.max(0, stepIndex) + 1).map(step => step.id);

const getNextIncompleteStepIndex = (tour: OnboardingTour, completedStepIds: string[], startIndex = 0) => {
    const completedSet = new Set(completedStepIds);

    for (let index = Math.max(0, startIndex); index < tour.steps.length; index += 1) {
        if (!completedSet.has(tour.steps[index].id)) {
            return index;
        }
    }

    return null;
};

const getNormalizedCompletedStepIds = (tour: OnboardingTour, completedStepIds: string[] | undefined, currentStepIndex?: number) => {
    const storedIndexes = (completedStepIds ?? [])
        .map(stepId => getStepIndexById(tour, stepId))
        .filter((stepIndex): stepIndex is number => stepIndex >= 0);
    const furthestCompletedIndex = storedIndexes.length ? Math.max(...storedIndexes) : -1;
    const fallbackCompletedIndex =
        furthestCompletedIndex >= 0
            ? furthestCompletedIndex
            : typeof currentStepIndex === "number" && currentStepIndex > 0
            ? currentStepIndex - 1
            : -1;

    return fallbackCompletedIndex >= 0 ? getCompletedStepIdsForIndex(tour, fallbackCompletedIndex) : [];
};

const getMergedCompletedStepIds = (tour: OnboardingTour, ...completedStepSets: Array<string[] | undefined>) => {
    const furthestCompletedIndex = completedStepSets.reduce((maxIndex, completedStepIds) => {
        const normalized = getNormalizedCompletedStepIds(tour, completedStepIds);
        return Math.max(maxIndex, normalized.length - 1);
    }, -1);

    return furthestCompletedIndex >= 0 ? getCompletedStepIdsForIndex(tour, furthestCompletedIndex) : [];
};

// DOM helpers
const isScrollableElement = (element: HTMLElement) => {
    const styles = window.getComputedStyle(element);
    const overflow = styles.overflow;
    const overflowY = styles.overflowY;
    const overflowX = styles.overflowX;

    return [overflow, overflowY, overflowX].some(value => value === "auto" || value === "scroll");
};

const getScrollContainer = (element: HTMLElement): HTMLElement | Window => {
    let current = element.parentElement;

    while (current) {
        const styles = window.getComputedStyle(current);
        // console.log("%c[onboarding][scroll] inspect ancestor", "color: #ddc914;", {
        //     tagName: current.tagName,
        //     className: current.className,
        //     overflow: styles.overflow,
        //     overflowY: styles.overflowY,
        //     overflowX: styles.overflowX,
        //     scrollHeight: current.scrollHeight,
        //     clientHeight: current.clientHeight,
        //     scrollTop: current.scrollTop,
        //     isScrollableElement: isScrollableElement(current),
        // });

        if (isScrollableElement(current)) {
            // console.log("%c[onboarding][scroll] selected container", "color: #0adf2e;", {
            //     tagName: current.tagName,
            //     className: current.className,
            //     scrollHeight: current.scrollHeight,
            //     clientHeight: current.clientHeight,
            //     scrollTop: current.scrollTop,
            // });
            return current;
        }

        current = current.parentElement;
    }

    onboardingLog("[onboarding][scroll] falling back to window");
    return window;
};

export const OnboardingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const location = useLocation();
    const { currentCoach } = useFetchCoach();
    const hasCompletedCompliance = currentCoach?.compliance?.termsAccepted === true;

    // Core tour state
    // Tracks which tour is active, where the user is inside it, and whether a step
    // is currently hidden or being positioned before we show the bubble.
    // DEV: Only used to bypass restarting tour on every page load during development.
    const [activeTourId, setActiveTourId] = useState<string | null>(null);
    const [isExitDialogOpen, setIsExitDialogOpen] = useState(false);
    const bubbleTransitionTimerRef = useRef<number | null>(null);
    const [isBubbleTransitioning, setIsBubbleTransitioning] = useState(false);
    const [transitionOriginStepId, setTransitionOriginStepId] = useState<string | null>(null);
    //const [activeTourId, setActiveTourId] = useState<string | null>("users-dashboard-tour");
    const [status, setStatus] = useState<OnboardingStatus>("idle");
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [completedStepIds, setCompletedStepIds] = useState<string[]>([]);
    const [hiddenStepId, setHiddenStepId] = useState<string | null>(null);
    const [positioningStepId, setPositioningStepId] = useState<string | null>(null);

    // Anchoring state
    // Stores the live DOM target and its latest measured rect for anchored bubbles/highlights.
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
    const [isBubbleInView, setIsBubbleInView] = useState(false);

    // Set to the step id when its target element cannot be found after the full
    // polling window (~10 × 300 ms). Cleared whenever the active step changes.
    const [isTargetMissingForStep, setIsTargetMissingForStep] = useState<string | null>(null);

    // Mutable refs
    // These keep non-visual bookkeeping out of React state.
    const missingTargetWarningsRef = useRef<Record<string, boolean>>({});
    // Always holds the latest nextStep so effects can call it without stale closures.
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    const nextStepRef = useRef<() => void>(() => {});
    const actionHandlersRef = useRef<Record<string, Record<string, () => void>>>({});
    // Firestore snapshots lag behind local writes. Keep the latest progress written
    // during this provider session so a stale coach snapshot cannot restore it.
    const sessionProgressRef = useRef<Record<string, StoredOnboardingProgress>>({});
    const autoScrolledStepIdRef = useRef<string | null>(null);
    const highlightedTargetRef = useRef<HTMLElement | null>(null);

    // Derived tour state
    const activeTour = getTour(activeTourId);
    const activeStep = activeTour?.steps[currentStepIndex] ?? null;
    const activeStepId = activeStep?.id ?? null;

    const doesStepMatchRoute = (step: OnboardingStep | null) => {
        if (!step) return false;
        if (step.route === location.pathname) return true;
        if (step.routePrefix && location.pathname.startsWith(step.routePrefix)) return true;
        return false;
    };

    // A step can be active for logic without being visible yet while we finish scrolling.
    const isActiveStepOnRoute = Boolean(activeStep && doesStepMatchRoute(activeStep) && status === "active" && hiddenStepId !== activeStepId);
    const isActiveStepVisible = Boolean(isActiveStepOnRoute && positioningStepId !== activeStepId);

    // Persistence helper
    const persistProgress = (
        tour: OnboardingTour,
        nextStatus: OnboardingStatus,
        nextIndex: number,
        nextCompletedStepIds: string[] = completedStepIds
    ) => {
        if (!currentCoach?.id) return;

        const progress = {
            status: nextStatus,
            currentStepIndex: nextIndex,
            version: tour.version,
            completedStepIds: nextCompletedStepIds,
        };

        // Keep a browser cache for immediate reads, but persist the authoritative
        // copy on the coach account so progress follows the coach across browsers.
        sessionProgressRef.current[tour.id] = progress;
        writeOnboardingProgress(currentCoach.id, tour, progress);

        void setDoc(
            doc(firestore, "Coaches", currentCoach.id),
            { onboardingProgress: { [tour.id]: progress } },
            // Replace this tour's map as a unit. A recursive merge can retain
            // obsolete step fields when the tour is restarted.
            { mergeFields: [new FieldPath("onboardingProgress", tour.id)] }
        ).catch(error => console.warn("persistOnboardingProgress() failed", error));
    };

    const completeStepForTour = (tour: OnboardingTour, stepId: string) => {
        const stepIndex = getStepIndexById(tour, stepId);
        if (stepIndex < 0) {
            return completedStepIds;
        }

        const nextCompletedStepIds = getCompletedStepIdsForIndex(tour, stepIndex);
        setCompletedStepIds(nextCompletedStepIds);
        return nextCompletedStepIds;
    };

    // Tour lifecycle actions
    const startTour = (tourId: string, options?: { force?: boolean; stepIndex?: number; status?: OnboardingStatus }) => {
        const tour = getTour(tourId);
        if (!tour || !currentCoach?.id) return false;
        if (!hasCompletedCompliance) return false;

        if (options?.force) {
            clearOnboardingProgress(currentCoach.id, tour.id);
            missingTargetWarningsRef.current = {};
            const forcedIndex = Math.min(options.stepIndex ?? 0, tour.steps.length - 1);
            const forcedStatus = options.status ?? "active";

            setActiveTourId(tour.id);
            setCurrentStepIndex(forcedIndex);
            setCompletedStepIds(getNormalizedCompletedStepIds(tour, [], forcedIndex));
            setStatus(forcedStatus);
            setHiddenStepId(null);
            onboardingLog(" *****force starting tour, resetting progress --->", {
                tourId: tour.id,
                stepIndex: forcedIndex,
                completedStepIds: getNormalizedCompletedStepIds(tour, [], forcedIndex),
            });
            persistProgress(tour, forcedStatus, forcedIndex, getNormalizedCompletedStepIds(tour, [], forcedIndex));
            return true;
        }

        const sessionProgress = sessionProgressRef.current[tour.id];
        const cloudProgress = currentCoach.onboardingProgress?.[tour.id];
        const stored = sessionProgress ?? cloudProgress ?? readOnboardingProgress(currentCoach.id, tour);

        // Upgrade existing local-only progress the first time this version runs.
        if (!cloudProgress && stored) {
            persistProgress(tour, stored.status, stored.currentStepIndex, stored.completedStepIds);
        }
        if (stored?.status === "dismissed" || stored?.status === "completed") {
            return false;
        }

        const normalizedCompletedStepIds = getNormalizedCompletedStepIds(tour, stored?.completedStepIds, stored?.currentStepIndex);
        const fallbackIndex = Math.min(stored?.currentStepIndex ?? options?.stepIndex ?? 0, tour.steps.length - 1);
        const nextIndex =
            getNextIncompleteStepIndex(tour, normalizedCompletedStepIds, fallbackIndex) ??
            getNextIncompleteStepIndex(tour, normalizedCompletedStepIds, 0);

        if (nextIndex === null) {
            persistProgress(tour, "completed", tour.steps.length - 1, normalizedCompletedStepIds);
            return false;
        }

        const nextStatus = options?.status ?? (stored?.status === "paused" ? "paused" : "active");

        setActiveTourId(tour.id);
        setCurrentStepIndex(nextIndex);
        setCompletedStepIds(normalizedCompletedStepIds);
        setStatus(nextStatus);
        setHiddenStepId(null);
        persistProgress(tour, nextStatus, nextIndex, normalizedCompletedStepIds);
        return true;
    };

    const markStepCompleted = (tourId: string, stepId: string) => {
        const tour = getTour(tourId);
        if (!tour) return;

        const stored = currentCoach?.id
            ? sessionProgressRef.current[tour.id] ?? currentCoach.onboardingProgress?.[tour.id] ?? readOnboardingProgress(currentCoach.id, tour)
            : null;
        const requestedCompletedStepIds = getCompletedStepIdsForIndex(tour, getStepIndexById(tour, stepId));
        const nextCompletedStepIds = getMergedCompletedStepIds(tour, completedStepIds, stored?.completedStepIds, requestedCompletedStepIds);

        setCompletedStepIds(nextCompletedStepIds);
        const nextIndex =
            getNextIncompleteStepIndex(tour, nextCompletedStepIds, getStepIndexById(tour, stepId)) ??
            getNextIncompleteStepIndex(tour, nextCompletedStepIds, 0);

        if (activeTourId === tour.id) {
            if (nextIndex === null) {
                setStatus("completed");
                setHiddenStepId(null);
                persistProgress(tour, "completed", tour.steps.length - 1, nextCompletedStepIds);
                setActiveTourId(null);
                setCurrentStepIndex(0);
                return;
            }

            const nextStatus = status === "paused" ? "paused" : "active";
            setCurrentStepIndex(nextIndex);
            setStatus(nextStatus);
            setHiddenStepId(null);
            persistProgress(tour, nextStatus, nextIndex, nextCompletedStepIds);
            return;
        }

        const normalizedStoredCompletedStepIds = getNormalizedCompletedStepIds(tour, stored?.completedStepIds, stored?.currentStepIndex);
        const storedIndexFloor = normalizedStoredCompletedStepIds.length ? normalizedStoredCompletedStepIds.length : stored?.currentStepIndex ?? 0;
        const nextStoredIndex = nextIndex === null ? Math.max(tour.steps.length - 1, 0) : Math.max(nextIndex, storedIndexFloor);
        const nextStoredStatus =
            nextIndex === null ? "completed" : stored?.status === "paused" ? "paused" : stored?.status === "dismissed" ? "dismissed" : "active";
        persistProgress(tour, nextStoredStatus, nextStoredIndex, nextCompletedStepIds);
    };

    const goToStep = (stepIndex: number, options?: { status?: OnboardingStatus }) => {
        onboardingLog("goToStep" + " ---> activeTour: ", activeTour);
        if (!activeTour) {
            onboardingLog("[onboarding][goToStep] no active tour, cannot go to step");
            return;
        }

        const requestedIndex = Math.max(0, Math.min(stepIndex, activeTour.steps.length - 1));
        const nextIndex = getNextIncompleteStepIndex(activeTour, completedStepIds, requestedIndex) ?? requestedIndex;
        const nextStatus = options?.status ?? "active";

        setCurrentStepIndex(nextIndex);
        setStatus(nextStatus);
        setHiddenStepId(null);
        persistProgress(activeTour, nextStatus, nextIndex);
    };

    const nextStep = () => {
        if (!activeTour) return;
        const nextCompletedStepIds = completeStepForTour(activeTour, activeTour.steps[currentStepIndex].id);
        const nextIndex = getNextIncompleteStepIndex(activeTour, nextCompletedStepIds, currentStepIndex + 1);

        if (nextIndex === null) {
            setStatus("completed");
            setHiddenStepId(null);
            persistProgress(activeTour, "completed", currentStepIndex, nextCompletedStepIds);
            setActiveTourId(null);
            setCurrentStepIndex(0);
            return;
        }

        setCurrentStepIndex(nextIndex);
        setStatus("active");
        setHiddenStepId(null);
        persistProgress(activeTour, "active", nextIndex, nextCompletedStepIds);
    };

    const beginBubbleTransition = () => {
        if (!activeStepId) return;
        setTransitionOriginStepId(activeStepId);
        setIsBubbleTransitioning(true);
        setHiddenStepId(activeStepId);
    };

    // Keep the ref current after every render so effects can call nextStep without
    // capturing a stale closure.
    useEffect(() => { nextStepRef.current = nextStep; });

    const nextStepAfterBriefDelay = (delayMs = BUBBLE_TRANSITION_DELAY_MS) => {
        if (!activeTour || !activeStepId) return;

        if (bubbleTransitionTimerRef.current !== null) window.clearTimeout(bubbleTransitionTimerRef.current);
        beginBubbleTransition();
        bubbleTransitionTimerRef.current = window.setTimeout(() => {
            bubbleTransitionTimerRef.current = null;
            nextStep();
        }, delayMs);
    };

    const prevStep = () => {
        if (!activeTour) return;

        const nextIndex = Math.max(currentStepIndex - 1, 0);
        setCurrentStepIndex(nextIndex);
        setStatus("active");
        setHiddenStepId(null);
        persistProgress(activeTour, "active", nextIndex);
    };

    const pauseTour = () => {
        if (!activeTour) return;

        setStatus("paused");
        setHiddenStepId(null);
        persistProgress(activeTour, "paused", currentStepIndex);
    };

    const resumeTour = () => {
        if (!activeTour) return;

        setStatus("active");
        setHiddenStepId(null);
        persistProgress(activeTour, "active", currentStepIndex);
    };

    const dismissTour = (tourId?: string) => {
        onboardingLog("dismissTour" + " ---> activeTour: ", activeTour);
        const resolvedTour = getTour(tourId ?? activeTourId);
        if (!resolvedTour) return;

        if (bubbleTransitionTimerRef.current !== null) {
            window.clearTimeout(bubbleTransitionTimerRef.current);
            bubbleTransitionTimerRef.current = null;
        }
        setIsBubbleTransitioning(false);
        setTransitionOriginStepId(null);

        setStatus("dismissed");
        setHiddenStepId(null);
        persistProgress(resolvedTour, "dismissed", currentStepIndex);
        setActiveTourId(null);
        setCurrentStepIndex(0);
    };

    const requestExitOnboarding = () => setIsExitDialogOpen(true);

    const confirmExitOnboarding = () => {
        setIsExitDialogOpen(false);
        dismissTour();
    };

    const completeTour = (tourId?: string) => {
        const resolvedTour = getTour(tourId ?? activeTourId);
        if (!resolvedTour) return;

        const resolvedStepId = resolvedTour.steps[Math.min(currentStepIndex, resolvedTour.steps.length - 1)]?.id;
        const nextCompletedStepIds = resolvedStepId ? completeStepForTour(resolvedTour, resolvedStepId) : completedStepIds;

        setStatus("completed");
        setHiddenStepId(null);
        persistProgress(resolvedTour, "completed", Math.max(resolvedTour.steps.length - 1, 0), nextCompletedStepIds);
        setActiveTourId(null);
        setCurrentStepIndex(0);
    };

    const restartTour = (tourId: string) => startTour(tourId, { force: true, stepIndex: 0 });

    // Custom action registry
    // Pages register step-specific actions here so the provider can stay generic.
    const registerActionHandler = (tourId: string, actionId: string, handler: () => void) => {
        if (!actionHandlersRef.current[tourId]) {
            actionHandlersRef.current[tourId] = {};
        }
        actionHandlersRef.current[tourId][actionId] = handler;
    };

    const unregisterActionHandler = (tourId: string, actionId: string) => {
        if (!actionHandlersRef.current[tourId]) return;
        delete actionHandlersRef.current[tourId][actionId];
    };

    const handleAction = (action: OnboardingButtonAction) => {
        switch (action.action) {
            case "next":
                nextStepAfterBriefDelay(action.transitionDelayMs);
                return;
            case "prev":
                prevStep();
                return;
            case "dismiss":
                dismissTour();
                return;
            case "complete":
                completeTour();
                return;
            case "custom": {
                if (!activeTourId || !action.actionId) return;
                const actionHandler = actionHandlersRef.current[activeTourId]?.[action.actionId];

                if (action.actionDelayMs) {
                    beginBubbleTransition();
                    bubbleTransitionTimerRef.current = window.setTimeout(() => {
                        bubbleTransitionTimerRef.current = null;
                        if (activeStepId) markStepCompleted(activeTourId, activeStepId);
                        actionHandler?.();
                    }, action.actionDelayMs);
                    return;
                }

                if (action.completionDelayMs) {
                    beginBubbleTransition();
                    actionHandler?.();
                    bubbleTransitionTimerRef.current = window.setTimeout(() => {
                        bubbleTransitionTimerRef.current = null;
                        if (activeStepId) markStepCompleted(activeTourId, activeStepId);
                    }, action.completionDelayMs);
                    return;
                }

                if (activeStepId) {
                    markStepCompleted(activeTourId, activeStepId);
                }
                if (action.actionId === "hide-add-client-modal-tip") {
                    if (activeStepId) setHiddenStepId(activeStepId);
                    return;
                }
                actionHandler?.();
                return;
            }
            default:
                return;
        }
    };

    // Target resolution
    // Polls for the current step target so anchored bubbles can survive async layout/render changes.
    useEffect(() => {
        if (!isActiveStepOnRoute || !activeStep?.target?.selector) {
            setAnchorEl(null);
            setTargetRect(null);
            return;
        }

        let cancelled = false;
        let pollCount = 0;

        const updateTarget = () => {
            if (cancelled) return;

            const nextAnchor = document.querySelector(activeStep.target?.selector ?? "") as HTMLElement | null;
            if (!nextAnchor) {
                pollCount += 1;
                if (pollCount >= 10 && !missingTargetWarningsRef.current[activeStep.id]) {
                    missingTargetWarningsRef.current[activeStep.id] = true;
                    onboardingWarn(`Onboarding target not found for step "${activeStep.id}" using selector "${activeStep.target?.selector}"`);
                    // Signal that the element genuinely doesn't exist in this layout
                    // (e.g. a desktop-only column viewed on mobile/tablet).
                    setIsTargetMissingForStep(activeStep.id);
                }
                setAnchorEl(null);
                setTargetRect(null);
                return;
            }

            const nextRect = nextAnchor.getBoundingClientRect();
            missingTargetWarningsRef.current[activeStep.id] = false;

            if (activeStep.id === "pending-client-create-first-ziv") {
                // Bubble 4 has been the most timing-sensitive step, so we keep a local
                // checkpoint here while tuning its target and scroll behavior.
            }

            if (highlightedTargetRef.current && highlightedTargetRef.current !== nextAnchor) {
                highlightedTargetRef.current.style.outline = "";
                highlightedTargetRef.current.style.outlineOffset = "";
            }

            // nextAnchor.style.outline = "3px solid #ff3b30";
            // nextAnchor.style.outlineOffset = "2px";
            highlightedTargetRef.current = nextAnchor;

            setAnchorEl(prevAnchor => (prevAnchor === nextAnchor ? prevAnchor : nextAnchor));
            setTargetRect(prevRect => {
                if (
                    prevRect &&
                    prevRect.top === nextRect.top &&
                    prevRect.left === nextRect.left &&
                    prevRect.width === nextRect.width &&
                    prevRect.height === nextRect.height
                ) {
                    return prevRect;
                }

                return nextRect;
            });
        };

        updateTarget();
        const intervalId = window.setInterval(updateTarget, 300);
        const handleWindowChange = () => updateTarget();

        window.addEventListener("resize", handleWindowChange);
        window.addEventListener("scroll", handleWindowChange, true);

        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
            window.removeEventListener("resize", handleWindowChange);
            window.removeEventListener("scroll", handleWindowChange, true);

            if (highlightedTargetRef.current) {
                highlightedTargetRef.current.style.outline = "";
                highlightedTargetRef.current.style.outlineOffset = "";
                highlightedTargetRef.current = null;
            }
        };
    }, [activeStep, isActiveStepOnRoute]);

    /* Auto-positioning
       Keeps the bubble hidden until the anchored target has settled into view. */
    useEffect(() => {
        if (!isActiveStepOnRoute || !activeStep?.target || !anchorEl || !targetRect || !activeStepId) return;
        if (autoScrolledStepIdRef.current === activeStepId) return;

        setPositioningStepId(activeStepId);

        const timeoutId = window.setTimeout(() => {
            if (autoScrolledStepIdRef.current === activeStepId) return;

            const viewportPadding = 24;
            const scrollContainer = getScrollContainer(anchorEl);
            const isWindowContainer = scrollContainer === window;
            const containerRect = isWindowContainer
                ? { top: 0, bottom: window.innerHeight }
                : (scrollContainer as HTMLElement).getBoundingClientRect();
            const liveRect = anchorEl.getBoundingClientRect();
            const isFullyVisible = liveRect.top >= containerRect.top + viewportPadding && liveRect.bottom <= containerRect.bottom - viewportPadding;

            // console.log("%c[onboarding][scroll] visibility check", "color: #052fe9;", {
            //     stepId: activeStepId,
            //     isWindowContainer,
            //     container: isWindowContainer
            //         ? {
            //               type: "window",
            //               innerHeight: window.innerHeight,
            //               scrollY: window.scrollY,
            //           }
            //         : {
            //               tagName: (scrollContainer as HTMLElement).tagName,
            //               className: (scrollContainer as HTMLElement).className,
            //               top: containerRect.top,
            //               bottom: containerRect.bottom,
            //               clientHeight: (scrollContainer as HTMLElement).clientHeight,
            //               scrollHeight: (scrollContainer as HTMLElement).scrollHeight,
            //               scrollTop: (scrollContainer as HTMLElement).scrollTop,
            //           },
            //     target: {
            //         top: liveRect.top,
            //         bottom: liveRect.bottom,
            //         height: liveRect.height,
            //     },
            //     viewportPadding,
            //     isFullyVisible,
            // });

            if (isFullyVisible) {
                onboardingLog("[onboarding][scroll] step is already fully visible, no need to scroll");
                setIsBubbleInView(true);
                autoScrolledStepIdRef.current = activeStepId;
                setPositioningStepId(currentStepId => (currentStepId === activeStepId ? null : currentStepId));
                return;
            }

            window.requestAnimationFrame(() => {
                const beforeScrollTop = isWindowContainer ? window.scrollY : (scrollContainer as HTMLElement).scrollTop;
                anchorEl.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                    inline: "nearest",
                });

                window.setTimeout(() => {
                    const afterScrollTop = isWindowContainer ? window.scrollY : (scrollContainer as HTMLElement).scrollTop;
                    onboardingLog("%c[onboarding][scroll] scroll result", "color: #e90510;", {
                        stepId: activeStepId,
                        isWindowContainer,
                        beforeScrollTop,
                        afterScrollTop,
                        changed: beforeScrollTop !== afterScrollTop,
                    });
                    setIsBubbleInView(true);
                    autoScrolledStepIdRef.current = activeStepId;
                    setPositioningStepId(currentStepId => (currentStepId === activeStepId ? null : currentStepId));
                }, 350);
            });
        }, 500);

        return () => {
            window.clearTimeout(timeoutId);
            setPositioningStepId(currentStepId => (currentStepId === activeStepId ? null : currentStepId));
        };
    }, [activeStep, activeStepId, anchorEl, isActiveStepOnRoute, targetRect]);

    // Step reset behavior
    // Clears step-specific positioning state whenever the active step changes.
    useLayoutEffect(() => {
        setIsTargetMissingForStep(null);

        if (!activeStep) {
            autoScrolledStepIdRef.current = null;
            setPositioningStepId(null);
            setIsBubbleInView(false);
            return;
        }

        if (autoScrolledStepIdRef.current && autoScrolledStepIdRef.current !== activeStep.id) {
            autoScrolledStepIdRef.current = null;
        }

        const waitsForTargetScroll = activeStep.id === "pending-client-create-first-ziv" || activeStep.waitForTargetScroll;
        if (!waitsForTargetScroll || autoScrolledStepIdRef.current !== activeStep.id) {
            setIsBubbleInView(false);
        }

        if (activeStep.target) {
            setPositioningStepId(activeStep.id);
            return;
        }

        setPositioningStepId(null);
    }, [activeStepId]);

    // Keep the overlay visible until the next step has an anchor. This prevents the
    // page from briefly flashing uncovered between consecutive onboarding bubbles.
    useEffect(() => {
        if (!isBubbleTransitioning || !activeStep || activeStep.id === transitionOriginStepId) return;

        const nextStepRequiresScroll = activeStep.id === "pending-client-create-first-ziv" || activeStep.waitForTargetScroll;
        const nextStepIsReady =
            !activeStep.target ||
            (isActiveStepOnRoute && Boolean(anchorEl) && (!nextStepRequiresScroll || isBubbleInView)) ||
            // Target confirmed missing after polling window: unblock the overlay.
            isTargetMissingForStep === activeStep.id;
        if (!nextStepIsReady) return;

        setIsBubbleTransitioning(false);
        setTransitionOriginStepId(null);
        setHiddenStepId(null);
    }, [activeStep, anchorEl, isActiveStepOnRoute, isBubbleInView, isBubbleTransitioning, transitionOriginStepId, isTargetMissingForStep]);

    // Auto-advance past steps whose target element doesn't exist in the current layout
    // (e.g. a desktop-only table column on mobile/tablet). Runs only after the overlay
    // has already been unblocked (isBubbleTransitioning = false) to avoid double-advance.
    useEffect(() => {
        if (!isTargetMissingForStep || isTargetMissingForStep !== activeStepId) return;
        if (!isActiveStepOnRoute || isBubbleTransitioning) return;
        nextStepRef.current();
    }, [isTargetMissingForStep, activeStepId, isActiveStepOnRoute, isBubbleTransitioning]);

    // Global keyboard dismissal
    useEffect(() => {
        if (!isActiveStepVisible) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                event.preventDefault();
                if (isExitDialogOpen) setIsExitDialogOpen(false);
                else requestExitOnboarding();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isActiveStepVisible, activeTourId, currentStepIndex, isExitDialogOpen]);

    const shouldRenderBubble =
        activeStep &&
        Boolean(
            status === "active" &&
                (doesStepMatchRoute(activeStep) || isBubbleTransitioning) &&
                (hiddenStepId !== activeStep.id || isBubbleTransitioning)
        );
    const waitsForTargetScroll = activeStep?.id === "pending-client-create-first-ziv" || activeStep?.waitForTargetScroll;
    const isBubbleOpen = waitsForTargetScroll ? isBubbleInView : !activeStep?.target || Boolean(anchorEl);

    return (
        <OnboardingContext.Provider
            value={{
                activeTourId,
                activeStep,
                status,
                startTour,
                markStepCompleted,
                goToStep,
                nextStep,
                prevStep,
                pauseTour,
                resumeTour,
                dismissTour,
                completeTour,
                restartTour,
                registerActionHandler,
                unregisterActionHandler,
            }}
        >
            {children}
            {shouldRenderBubble ? (
                <OnboardingBubble
                    isOpen={isBubbleOpen || isBubbleTransitioning}
                    anchorEl={anchorEl}
                    targetRect={targetRect}
                    hideContent={isBubbleTransitioning}
                    step={activeStep}
                    stepIndex={currentStepIndex}
                    totalSteps={activeTour?.steps.length ?? 0}
                    onAction={handleAction}
                    onDismiss={requestExitOnboarding}
                />
            ) : null}
            <Dialog
                open={isExitDialogOpen}
                onClose={() => setIsExitDialogOpen(false)}
                sx={{ zIndex: 1700 }}
                aria-labelledby="exit-onboarding-title"
                aria-describedby="exit-onboarding-description"
                PaperProps={{
                    sx: {
                        width: 340,
                        maxWidth: "calc(100vw - 32px)",
                        bgcolor: "#202020 !important",
                        color: "#F6F7FB",
                        border: "1px solid rgba(255,255,255,0.16)",
                        borderRadius: 3,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                    },
                }}
            >
                <DialogTitle id="exit-onboarding-title" sx={{ pb: 2.75, fontSize: "1.15rem", fontWeight: 700 }}>
                    Exit onboarding?
                </DialogTitle>
                <DialogContent sx={{ pt: "8px !important", pb: 2.5, fontSize: "0.875rem", fontWeight: 400, textAlign: "center" }}>
                    <Typography
                        id="exit-onboarding-description"
                        variant="body2"
                        sx={{ color: "rgba(255,255,255,0.78)", lineHeight: 1.6, fontSize: "0.75rem" }}
                    >
                        You can restart the walkthrough anytime from:
                    </Typography>
                    <Box
                        sx={{
                            mt: 1.5,
                            px: 1.5,
                            py: 1.25,
                            fontSize: "0.75rem",

                            borderRadius: 1.5,
                            bgcolor: "rgba(255,255,255,0.08)",
                            border: "1px solid rgba(255,255,255,0.16)",
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                color: "#fff",
                                fontWeight: 700,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                gap: 0,
                                fontSize: "0.75rem",
                            }}
                        >
                            Settings → Product onboarding
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pt: 2, pb: 2.5, gap: 1 }}>
                    <Button variant="contained" onClick={() => setIsExitDialogOpen(false)} autoFocus sx={{ textTransform: "none" }}>
                        Keep onboarding
                    </Button>
                    <Button
                        variant="outlined"
                        color="inherit"
                        onClick={confirmExitOnboarding}
                        sx={{ textTransform: "none", borderColor: "rgba(255,255,255,0.38)", "&:hover": { borderColor: "rgba(255,255,255,0.72)" } }}
                    >
                        Exit onboarding
                    </Button>
                </DialogActions>
            </Dialog>
        </OnboardingContext.Provider>
    );
};

export const useOnboarding = () => {
    const context = useContext(OnboardingContext);
    if (!context) {
        throw new Error("useOnboarding must be used within an OnboardingProvider");
    }
    return context;
};
